'use client'

import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { CheckCircle2, ArrowRight } from 'lucide-react'

interface AutomotiveProofSectionProps {
  eyebrow?: string
  statement: string
  supporting: string
  linkHref: string
  linkLabel: string
}

export function AutomotiveProofSection({ eyebrow, statement, supporting, linkHref, linkLabel }: AutomotiveProofSectionProps) {
  return (
    <section className="relative py-24 overflow-hidden bg-gradient-to-br from-[#2F5FE8]/5 via-background to-background">
      <div className="container mx-auto px-6">
        <div className="grid md:grid-cols-[1.2fr,0.8fr] gap-10 items-center max-w-7xl mx-auto">
          {/* Left Column */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="reveal"
          >
            {eyebrow && (
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/10 border border-green-500/20 mb-6">
                <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                <span className="text-sm font-semibold text-green-600 dark:text-green-400 uppercase tracking-wide">
                  {eyebrow}
                </span>
              </div>
            )}
            <h3 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              {statement}
            </h3>
            <p className="text-lg text-muted-foreground mb-8">
              {supporting}
            </p>
            <Link
              to={linkHref}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-border bg-background text-foreground font-semibold text-sm transition-all duration-300 hover:bg-muted hover:scale-[1.02]"
            >
              {linkLabel}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </motion.div>

          {/* Right Column - How it works */}
          <motion.div
            initial={{ opacity: 0, x: 30, filter: "blur(10px)" }}
            whileInView={{ opacity: 1, x: 0, filter: "blur(0px)" }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
            className="reveal reveal-delay-1 group lift-hover relative rounded-3xl overflow-hidden bg-white dark:bg-zinc-900 p-8 transition-all duration-300"
            style={{
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            }}
          >
            {/* Gradient Glow */}
            <motion.div
              className="absolute bottom-0 left-0 right-0 h-2/3 opacity-60 group-hover:opacity-100 transition-opacity duration-300"
              style={{
                background: 'radial-gradient(ellipse at bottom, rgba(47, 95, 232, 0.15) -20%, transparent 70%)',
                filter: 'blur(40px)',
              }}
            />

            {/* Bottom Border Glow */}
            <motion.div
              className="absolute bottom-0 left-0 right-0 h-[2px]"
              style={{
                background: 'linear-gradient(90deg, rgba(47, 95, 232, 0.1) 0%, rgba(47, 95, 232, 0.5) 50%, rgba(47, 95, 232, 0.1) 100%)',
                boxShadow: '0 0 15px 3px rgba(47, 95, 232, 0.2)',
              }}
            />

            {/* Content */}
            <div className="relative z-10">
              <p className="text-sm font-bold uppercase tracking-[0.35em] text-muted-foreground mb-6">
                How it works
              </p>
              <ul className="space-y-4 text-sm text-muted-foreground">
                <li className="reveal reveal-delay-0 flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#2F5FE8]/10 flex items-center justify-center text-xs font-bold text-[#2F5FE8]">
                    1
                  </span>
                  <span>Map your current calls, chats, and social DMs.</span>
                </li>
                <li className="reveal reveal-delay-1 flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#2F5FE8]/10 flex items-center justify-center text-xs font-bold text-[#2F5FE8]">
                    2
                  </span>
                  <span>Launch missed-call text-back, AI responders, and review flows.</span>
                </li>
                <li className="reveal reveal-delay-2 flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#2F5FE8]/10 flex items-center justify-center text-xs font-bold text-[#2F5FE8]">
                    3
                  </span>
                  <span>Train your advisors/BDC with a guided playbook and dashboards.</span>
                </li>
              </ul>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
